import { FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Box, VStack, HStack, Text, Input, Button, Spinner } from '@chakra-ui/react';
import { BusinessInfoInput } from '@/types/business';
import { businessService } from '@/services/firebase/businessService';
import { useAuthStore } from '@/stores/authStore';
import { AppLayout } from '@/components/layout/AppLayout';
import { toaster } from '@/components/ui/toaster';

/**
 * 事業者管理ページ（管理者のみ）
 * システム共有の事業者名・犬舎名（settings/business）を編集する
 */
export const BusinessPage: FC = () => {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<BusinessInfoInput>();

  // 既存の事業者情報をプレフィル（未作成なら空フォームのまま）
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const info = await businessService.getBusinessInfo();
        if (cancelled) return;
        if (info) {
          setValue('businessName', info.businessName);
          setValue('kennelName', info.kennelName);
        }
      } catch (error) {
        if (!cancelled) {
          toaster.error({
            title: '事業者情報の取得に失敗しました',
            description: error instanceof Error ? error.message : undefined,
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setValue]);

  const handleFormSubmit = async (values: BusinessInfoInput) => {
    if (!user) return;

    setIsSubmitting(true);
    try {
      await businessService.saveBusinessInfo(
        { businessName: values.businessName, kennelName: values.kennelName },
        user.uid
      );
      toaster.success({ title: '事業者情報を保存しました' });
    } catch (error) {
      toaster.error({
        title: '事業者情報の保存に失敗しました',
        description: error instanceof Error ? error.message : undefined,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <VStack gap={4} py={8}>
          <Spinner size="xl" />
          <Text>読み込み中...</Text>
        </VStack>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <VStack align="stretch" gap={6} maxW="xl">
        {/* ヘッダー */}
        <Text fontSize="2xl" fontWeight="bold">
          事業者管理
        </Text>

        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <VStack gap={6} align="stretch">
            {/* 事業者名 */}
            <Box>
              <Text asChild fontSize="sm" fontWeight="medium" mb={2} display="block">
                <label htmlFor="business-name">
                  事業者名 <Text as="span" color="red.500">*</Text>
                </label>
              </Text>
              <Input
                id="business-name"
                {...register('businessName', {
                  required: '事業者名は必須です',
                  maxLength: { value: 100, message: '事業者名は100文字以内で入力してください' },
                })}
              />
              {errors.businessName && (
                <Text fontSize="sm" color="red.500" mt={1}>
                  {errors.businessName.message}
                </Text>
              )}
            </Box>

            {/* 犬舎名 */}
            <Box>
              <Text asChild fontSize="sm" fontWeight="medium" mb={2} display="block">
                <label htmlFor="kennel-name">
                  犬舎名 <Text as="span" color="red.500">*</Text>
                </label>
              </Text>
              <Input
                id="kennel-name"
                {...register('kennelName', {
                  required: '犬舎名は必須です',
                  maxLength: { value: 100, message: '犬舎名は100文字以内で入力してください' },
                })}
              />
              {errors.kennelName && (
                <Text fontSize="sm" color="red.500" mt={1}>
                  {errors.kennelName.message}
                </Text>
              )}
            </Box>

            {/* ボタン */}
            <HStack justify="flex-end">
              <Button type="submit" colorPalette="blue" loading={isSubmitting}>
                保存
              </Button>
            </HStack>
          </VStack>
        </form>
      </VStack>
    </AppLayout>
  );
};

export default BusinessPage;
